import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, Heading, Label, Paragraph, Spinner, tokens } from '@treams/treams-ui';

import EvaluationSubmissionGraph from './EvaluationSubmissionGraph';
import EvaluationSubmissionScoreModal from './EvaluationSubmissionScoreModal';
import EvaluationSubmissionSection from './EvaluationSubmissionSection';

const Placeholder = styled.div`
	color: ${tokens.text.secondary};
	font-size: ${tokens.typography.sizes.body}px;
	text-align: center;
`;

export default function EvaluationSubmissionScore(props: {
	submission: graphql.EvaluationSubmission;
	saving?: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const canOverrideFinalScore = useFeature(graphql.FeatureName.EvaluationsOverride);

	const finalScoreItem = props.submission.evaluationForm.sections
		.flatMap((section) => section.items)
		.find((item) => item.type === graphql.EvaluationSectionItemType.FinalScore);

	const [reset, { loading }] = graphql.useOverrideEvaluationSubmissionScoreMutation({
		variables: {
			input: {
				id: props.submission.id,
				competencyLevelID: undefined,
				reason: undefined,
			},
		},
	});

	const [modal, setModal] = useState(false);

	if (!finalScoreItem) {
		return null;
	}

	const isManager =
		props.submission.viewer.party === graphql.EvaluationSubmissionParty.Manager &&
		props.submission.viewer.viewerCanUpdate;

	const showFinalScore =
		(props.submission.manager.hasShared && props.submission.employee.hasShared) ||
		isManager ||
		props.submission.viewerCanOverrideScore;

	if (!showFinalScore) {
		return null;
	}

	const competencyLevelSet = finalScoreItem.finalScore?.competencyLevelSet;
	const displayLevelSet = finalScoreItem.finalScore?.displayLevelSet;

	if (!competencyLevelSet) {
		return null;
	}

	const levels = displayLevelSet?.levels ?? competencyLevelSet.levels;

	const mappedSections = props.submission.evaluationForm.sections.map((section, index) => ({
		name:
			localize(
				section.items.find((item) => item.type === graphql.EvaluationSectionItemType.Title)?.title
					?.text ?? [],
			)?.text ?? `§ ${index + 1}`,
		employee: props.submission.employee.sections[index].score,
		manager: props.submission.manager.sections[index].score,
	}));

	const sections = mappedSections
		.filter(
			({ name, employee, manager }) =>
				!!name &&
				((employee !== null && employee !== undefined) ||
					(manager !== null && manager !== undefined)),
		)
		.filter(({ manager }) => manager !== null && manager !== undefined);

	if (sections.length === 0) {
		return (
			<EvaluationSubmissionSection mb={4}>
				<Placeholder>{t('evaluation.submission.finalScore.placeholder')}</Placeholder>
			</EvaluationSubmissionSection>
		);
	}

	const finalScore = props.submission.overrideScore ?? props.submission.manager.finalScore;
	// Sum and decimal scores are not one of the levels, so they have no match.
	const matchIndex = finalScore ? levels.findIndex(({ id }) => finalScore.id === id) : -1;
	const match = matchIndex >= 0 ? matchIndex : undefined;
	const matchedLevel = match !== undefined ? levels[match] : undefined;
	const isSum =
		finalScoreItem.finalScore?.calculationMethod ===
		graphql.EvaluationFinalScoreCalculationMethod.Sum;

	const finalScoreText = finalScore
		? localize(finalScore.title).text
		: matchedLevel
			? localize(matchedLevel.title).text
			: '';

	if (props.saving) {
		return (
			<EvaluationSubmissionSection mb={4}>
				<Spinner centered />
			</EvaluationSubmissionSection>
		);
	}

	return (
		<EvaluationSubmissionSection mb={4}>
			{modal && (
				<EvaluationSubmissionScoreModal
					submission={props.submission}
					competencyLevelSet={displayLevelSet ?? competencyLevelSet}
					saving={props.saving ?? false}
					onDismiss={() => setModal(false)}
				/>
			)}
			<Flex justifyContent="space-between" alignItems="center" mb={3}>
				<Heading>{t('evaluation.form.scoreGraph.finalScore.label')}</Heading>
				{loading ? (
					<Spinner centered />
				) : (
					<Flex ml={3}>
						{props.submission.viewerCanOverrideScore && canOverrideFinalScore && (
							<Button
								small
								title={t('evaluation.submission.overrideScore.button.title')}
								onClick={() => setModal(true)}
							/>
						)}
						{props.submission.overrideScore && (
							<Box ml={2}>
								<Button
									small
									type="secondary"
									title={t('evaluation.submission.overrideScore.resetButton.title')}
									onClick={() => {
										reset();
									}}
								/>
							</Box>
						)}
					</Flex>
				)}
			</Flex>
			{!isSum && (
				<EvaluationSubmissionGraph
					levels={levels.map((level) => localize(level.title).text)}
					sections={sections
						.filter((section) => section.manager !== null && section.manager !== undefined)
						.map((section) => ({
							score: section.manager!,
							name: section.name,
						}))}
					match={match}
				/>
			)}

			{props.submission.overrideScore ? (
				<Paragraph>
					<strong>{localize(props.submission.overrideScore.title).text}</strong> (
					{t('evaluation.submission.overrideScore.status.title')})
				</Paragraph>
			) : (
				<Paragraph>
					<strong>{finalScoreText}</strong>
				</Paragraph>
			)}
			{props.submission.overrideReason && (
				<Box mt={3}>
					<Label>{t('evaluation.submission.overrideScore.modal.reason.label')}</Label>
					<Paragraph>{props.submission.overrideReason}</Paragraph>
				</Box>
			)}
		</EvaluationSubmissionSection>
	);
}
